/**
 * Cloud sync, load and save the persisted app-state JSON blob.
 *
 * The shape of the JSON is identical to what we put in localStorage: a
 * blob produced by `encodeForStorage` (Decimal to "dec:...", Date to "dt:...",
 * etc.). Storing the same shape both places lets the same encode/decode
 * pipeline serve both targets, and means cloud and local can be swapped
 * freely without re-shaping data.
 *
 * Patch AP: closed the post-AO wipe perpetuation. Two changes:
 *
 *   1. cloudLoaded gate. scheduleCloudSave no-ops until AuthGate flips
 *      the flag on. Pre-AP, any saveToStorage call during AuthGate's
 *      load gap (or worse, during a subscribed-but-not-yet-applied
 *      realtime event) could capture the default/in-flight state and
 *      push it to cloud, perpetuating a wipe. The gate prevents that
 *      entire class of race.
 *
 *   2. saveStateToCloudImmediate. Critical recovery actions (resetToSeed,
 *      importFromJson) need a write path that bypasses the debounce.
 *      Without this, a setLiveSnapshot firing within the debounce
 *      window can cancel the queued save and the recovery never
 *      reaches cloud. The immediate path also bypasses the gate, since
 *      these are explicit user actions that should always commit.
 *
 * Patch BQ: debounce dropped from 1500ms to 500ms. The longer window
 * made the "saved" indicator lag visibly behind user edits. 500ms still
 * coalesces rapid keystrokes into a single network call (typing three
 * separate fields in under 500ms is unrealistic) but feels instant.
 * Also added installBeforeUnloadFlush() so closing the tab during a
 * pending debounce doesn't lose the last edit; AuthGate wires it up
 * once on mount.
 *
 * Patch EL: fixed PWA wake save failure. Replaced the outer 20s side-
 * effecting watchdog setTimeout in flushCloudSave with a Promise.race
 * outer timeout. The old watchdog set watchdogFired=true, cleared
 * inFlight, notified error via callback -- then catch hit the guard
 * `if (watchdogFired) return` and exited before the retry path could
 * run. Promise.race surfaces the timeout as a thrown error so catch
 * handles it normally and the retry fires. Also removed the
 * withinWakeWindow gate from the retry condition (retry now fires on
 * any timeout-style error, covering returns from background more than
 * 30s after the wake event) and widened the error regex to include
 * 'abort' (covers AbortError on iOS Safari).
 *
 * Patch EM: fixed retry also timing out on semi-stale PWA. Two problems:
 *
 *   1. saveStateToCloud used Promise.race but never aborted the underlying
 *      fetch when the timeout won. The hung fetch kept the dead socket
 *      checked out from the connection pool, so EL's retry immediately
 *      got the same stale connection and also timed out. Fix: AbortController
 *      wired to the upsert via .abortSignal() (must precede .single() in
 *      the chain). When the 15s timer fires, controller.abort() is called
 *      first, which tells the browser to release the socket before the
 *      timeout error propagates to the caller. The retry then opens a fresh
 *      connection.
 *
 *   2. The prewarm query in installResumeListener only fired when
 *      hiddenFor > 60_000ms. "Semi-stale" (30-50s background, common when
 *      the user locks their phone briefly) never triggered it, so the
 *      dead socket was still in the pool when the user's first edit arrived.
 *      Fix: lower the threshold to 5_000ms so any meaningful background
 *      period flushes the socket pool before the save.
 *
 *   Also widened the retry regex to include 'load failed' (iOS Safari's
 *   error string for hard network failures).
 *
 * Patch EN: fixed the retry also hitting the dead connection. EM added
 * AbortController to saveStateToCloud so the save stream is cancelled at
 * 15s, but the prewarm SELECT (fired on wake) had no AbortController.
 * With one stream still active (the hanging prewarm), the browser could
 * not close the HTTP/2 connection, so the retry immediately reused the
 * same dead socket and also timed out. Fix: added AbortController with an
 * 8s timeout to the prewarm so it is cancelled BEFORE the save stream.
 * Once both streams are closed the browser tears down the dead connection
 * and the retry opens a fresh one. Also added a 500ms pause in the retry
 * path to give the browser time to complete the teardown before the next
 * connect attempt.
 *
 * Patch EO: two remaining failure modes on iOS wake:
 *
 *   1. JWT expiry. Access tokens live 1 hour. A PWA that stayed backgrounded
 *      long enough will have an expired token. Supabase PostgREST returns a
 *      401/PGRST301 immediately (not a timeout), so the retry regex never
 *      matched and the save surfaced an error with no retry. Fix: added an
 *      auth error branch to the retry condition (jwt|expired|token|
 *      unauthorized|invalid.*claim) and call supabase.auth.refreshSession()
 *      (raced with a 2s timeout) before the retry so the next upsert carries
 *      a fresh Bearer token.
 *
 *   2. Proactive session refresh for all retries. Even on timeout errors the
 *      session might be borderline stale. The refreshSession call races with
 *      2s so a dead auth server does not block the retry; it returns in
 *      <300ms when healthy since it is a single POST.
 *
 * Patch EQ: removed supabase.auth.refreshSession() from the retry path.
 * refreshSession() has no AbortController support. On a dead HTTP/2
 * connection to the Supabase auth server it hangs indefinitely, keeping
 * the connection "active" in the browser pool even after the save and
 * prewarm streams were cleanly aborted. The retry then reused the same
 * dead socket. Fix: removed the call and increased the settle pause from
 * 500ms to 1500ms. supabase-js handles JWT expiry inline on the next
 * saveStateToCloud call using a fresh connection.
 *
 * Patch ER: three changes:
 *
 *   1. Settle pause increased from 1500ms to 3000ms.
 *   2. outerTimeoutHandle cleared at the START of the catch block.
 *   3. Error written to localStorage and shown in the sync indicator.
 *
 * Patch ES: root-cause fix for the iOS PWA retry also timing out.
 *
 *   AbortController cancels the HTTP/2 STREAM but does NOT close the
 *   underlying TCP connection. iOS Safari keeps the dead socket in the
 *   origin-keyed connection pool after a stream abort. Any request to the
 *   same origin therefore reuses the dead socket and hangs again.
 *
 *   iOS TCP retransmit backoff (RFC 6298, initial RTO ~1s on iOS mobile):
 *     retransmit 1: +1s  cumulative 1s
 *     retransmit 2: +2s  cumulative 3s
 *     retransmit 3: +4s  cumulative 7s
 *     retransmit 4: +8s  cumulative 15s  <-- our AbortController fires
 *     retransmit 5: +16s cumulative 31s  <-- OS evicts dead socket
 *
 *   The prewarm fires at t=0 (wake) and triggers the first TCP send.
 *   Our save fires at ~t=0.5s; AbortController fires at ~t=15.5s.
 *   iOS evicts the dead socket at ~t=31s (retransmit 5 timeout).
 *   Required settle after our abort: 31 - 15.5 = 15.5s. We use 20s.
 *
 *   After the 20s settle a lightweight probe SELECT runs (5s timeout)
 *   to confirm the connection is live before committing the full upsert.
 *   If the probe also hangs (iOS needed 63s for 6 retransmits, rare),
 *   we retry anyway and show the error if it also fails.
 *
 * Patch ET: eliminated the iOS timer-drop hang in the catch block.
 *
 *   The ES approach awaited a 20s Promise (via setTimeout) inside the catch
 *   block. If iOS suspended JS while the app was backgrounded during that
 *   20s window (phone locked), the setTimeout callback was dropped. The
 *   Promise never resolved, inFlight stayed true, and the UI was permanently
 *   stuck on 'saving...' until the user force-quit the PWA.
 *
 *   Fix: the catch block now returns immediately after calling notify('error').
 *   A 60s retryTimer (plain setTimeout at module scope) schedules the next
 *   attempt outside the async call stack. If iOS drops this timer during
 *   suspension, it fires late on resume -- not hang forever -- because
 *   macrotask timers fire on wake even when delayed. The 60s window allows
 *   the full iOS TCP retransmit backoff (up to ~63s for 6 retransmits) to
 *   complete before the retry opens a fresh connection.
 *
 *   If the user makes an edit before 60s elapses, scheduleCloudSave cancels
 *   the retryTimer and the debounced save takes over. The settling flag and
 *   the inline probe are removed; they are not needed when the catch block
 *   does not await anything.
 *
 * Patch EV: switched from a fixed 60s retry delay to progressive backoff.
 *
 *   The 60s fixed delay assumed iOS TCP retransmit backoff completes within
 *   31s (5 retransmits, RFC 6298 with initial RTO ~1s). But some iOS
 *   configurations run up to 6 retransmits (~63s). When the retry fired at
 *   t=75s on a device with a 6-retransmit configuration, the dead socket was
 *   still in the pool and the retry timed out again at t=90s.
 *
 *   Progressive delays: 20s -> 40s -> 60s -> 80s -> 100s -> 120s (capped).
 *   First retry fires at t=35s (save timeout 15s + 20s delay). For standard
 *   5-retransmit iOS (eviction at t=31s), the fresh connection is ready 4s
 *   early and the first retry succeeds. For 6-retransmit iOS (eviction at
 *   t=63s), the first retry at t=35s fails, but the second at t=35+15+40=90s
 *   succeeds. retryAttempt resets to 0 on success or user edit so each new
 *   failure sequence starts fresh from the 20s base.
 *
 * Patch EY: reload after the first timeout instead of the second, and
 * raise the inner save timeout from 15s to 20s. Combined these reduce the
 * worst-case wait before the page-reload recovery from ~50s (15s timeout +
 * 20s delay + second 15s timeout) to ~20s (one 20s timeout then reload).
 * The outer flushCloudSave safety net is raised to 25s to stay above the
 * inner. Proactive 3-minute reload (EX) already prevents the stale-socket
 * scenario on mobile; EY tightens the reactive fallback for cases that
 * slip through (desktop, or mobile sessions shorter than 3 min).
 *
 * Patch EU: added a 5s heartbeat interval as a final safety net against
 * stuck saves.
 *
 *   ET fixed the catch-block hang. But if BOTH the 15s inner timer AND the
 *   20s outer timer are dropped by iOS during suspension, the save awaits
 *   never reject and inFlight stays true even after ET. Additionally,
 *   visibilitychange is known to be unreliable on some iOS PWA versions
 *   (WebKit bug: event fires late or not at all on bfcache restore paths).
 *
 *   A setInterval firing every 5s checks if inFlight has been true for
 *   more than 20s. If so, it calls resumeRecover() to force-reset the
 *   stuck state and kick a fresh save. setInterval callbacks on iOS are
 *   deferred during suspension but NOT dropped -- they fire as a batch on
 *   resume. So a user who backgrounds the app for 60s will see the interval
 *   fire on wake, detect the stuck save (elapsed >> 20s), and recover
 *   immediately rather than remaining stuck.
 *
 *   Combined with visibilitychange, pageshow, and the scheduleCloudSave
 *   self-heal, this gives four independent recovery paths. All four must
 *   fail simultaneously for a save to remain stuck.
 */

import { supabase } from './client.js';

interface AppStateRow {
  user_id: string;
  tenant: string;
  state: unknown;
  updated_at: string;
}

/** Load a user's persisted state from Supabase for a specific tenant site. */
/** Converts any thrown value to a proper Error with a readable message.
 *  Supabase returns plain objects { code, message, details, hint } that are
 *  NOT Error instances; String() on them gives '[object Object]'. */
function toError(e: unknown): Error {
  if (e instanceof Error) return e;
  if (typeof e === 'object' && e !== null) {
    const obj = e as Record<string, unknown>;
    const msg = typeof obj.message === 'string' ? obj.message
      : typeof obj.code === 'string' ? `${obj.code}${typeof obj.details === 'string' && obj.details ? ': ' + obj.details : ''}`
      : (() => { try { return JSON.stringify(e); } catch { return String(e); } })();
    return new Error(msg);
  }
  return new Error(String(e));
}

/** Returns true for any error code indicating the tenant column is absent. */
function isMissingColumnError(e: unknown): boolean {
  if (typeof e !== 'object' || e === null) return false;
  const code = 'code' in e ? (e as { code: unknown }).code : undefined;
  return code === '42703' || code === 'PGRST204';
}

export async function loadStateFromCloud(userId: string, tenant: string): Promise<unknown | null> {
  if (!supabase) return null;

  // 15s timeout mirrors the save-side inner timeout. Without it, a hanging
  // network request leaves cloudLoadStatus stuck at 'loading' forever and
  // the user sees a black screen they can never escape without a hard reload.
  const loadCtrl = new AbortController();
  const loadTimeout = setTimeout(() => loadCtrl.abort(), 15_000);

  try {
    const { data, error } = await supabase
      .from('app_state')
      .select('state, updated_at')
      .eq('user_id', userId)
      .eq('tenant', tenant)
      .abortSignal(loadCtrl.signal)
      .maybeSingle();

    // Patch AP backward-compat: tenant column absent (migration AP not yet run).
    // PostgREST returns PGRST204 for unknown columns; PostgreSQL returns 42703.
    // Retry without the tenant filter so sign-in works until migration runs.
    if (error) {
      if (isMissingColumnError(error)) {
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('app_state')
          .select('state, updated_at')
          .eq('user_id', userId)
          .abortSignal(loadCtrl.signal)
          .maybeSingle();
        if (fallbackError) throw toError(fallbackError);
        if (!fallbackData) return null;
        return (fallbackData as Pick<AppStateRow, 'state' | 'updated_at'>).state;
      }
      throw toError(error);
    }

    if (!data) return null;
    return (data as Pick<AppStateRow, 'state' | 'updated_at'>).state;
  } finally {
    clearTimeout(loadTimeout);
    loadCtrl.abort();
  }
}

/** Upsert a user's state row for a specific tenant site. Idempotent, safe to call repeatedly. */
export async function saveStateToCloud(userId: string, tenant: string, state: unknown): Promise<void> {
  if (!supabase) return;

  // Patch DF: 15s timeout via Promise.race to fix the PWA stuck-at-
  // saving bug. Without this, a network drop mid-fetch leaves the
  // underlying fetch in a zombie state: await never resolves, inFlight
  // stays permanently true, UI is stuck on 'saving' forever.
  //
  // Patch EM: added AbortController so the timeout actually cancels the
  // fetch. Previously Promise.race only stopped US from waiting; the
  // hung fetch kept the dead socket checked out from the pool, and EL's
  // retry immediately got the same stale connection. Now controller.abort()
  // fires when the timer fires, releasing the socket before the error
  // propagates. The retry gets a fresh connection.
  //
  // .abortSignal() must precede .single() in the postgrest-js chain;
  // .single() returns a PromiseLike that no longer exposes abortSignal.
  const controller = new AbortController();
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(() => {
      controller.abort();
      reject(new Error('Save timed out after 12s. Check connection.'));
    }, 12_000);
  });

  // Patch AP: three-tier backward-compat for the tenant isolation migration.
  //
  //   Tier 1 (fully migrated): column + constraint both exist. tryUpsert with
  //     onConflict:'user_id,tenant' succeeds immediately.
  //
  //   Tier 2 (column exists, constraint not yet): tryUpsert fails with 42P10
  //     ("no unique constraint matching ON CONFLICT"). Retry with onConflict:'user_id'.
  //
  //   Tier 3 (column does not exist): tryUpsert fails with 42703
  //     ("undefined_column"). Fall back to tryUpsertLegacy which omits the
  //     tenant field entirely and targets the old user_id-only constraint.
  //
  // Once migration AP has run, tiers 2 and 3 are never reached.
  const tryUpsert = async (onConflict: string): Promise<{ data: unknown; error: unknown }> => {
    return supabase!
      .from('app_state')
      .upsert({ user_id: userId, tenant, state }, { onConflict })
      .select('updated_at')
      .abortSignal(controller.signal)
      .single() as unknown as Promise<{ data: unknown; error: unknown }>;
  };

  const tryUpsertLegacy = async (): Promise<{ data: unknown; error: unknown }> => {
    return supabase!
      .from('app_state')
      .upsert({ user_id: userId, state }, { onConflict: 'user_id' })
      .select('updated_at')
      .abortSignal(controller.signal)
      .single() as unknown as Promise<{ data: unknown; error: unknown }>;
  };

  try {
    let result = await Promise.race([tryUpsert('user_id,tenant'), timeoutPromise]);
    if (result.error) {
      const code = typeof result.error === 'object' && result.error !== null && 'code' in result.error
        ? (result.error as { code: string }).code : '';
      if (code === '42P10') {
        // Tier 2: column exists but composite constraint absent  -  retry with user_id-only conflict.
        result = await Promise.race([tryUpsert('user_id'), timeoutPromise]);
      } else if (code === '42703' || code === 'PGRST204') {
        // Tier 3: column absent entirely (PostgreSQL 42703 or PostgREST PGRST204 schema-cache
        // miss)  -  omit tenant field entirely and target the old user_id-only constraint.
        result = await Promise.race([tryUpsertLegacy(), timeoutPromise]);
      }
    }
    const { data, error } = result as { data: unknown; error: unknown };
    if (error) throw toError(error);
    if (data && typeof (data as { updated_at?: string }).updated_at === 'string') {
      lastSelfWriteAt = (data as { updated_at: string }).updated_at;
    }
  } finally {
    if (timeoutHandle) clearTimeout(timeoutHandle);
    controller.abort();
  }
}

/** Timestamp (ISO string) of our most recent successful upsert. The realtime
 *  subscription compares incoming row.updated_at against this to skip
 *  applying our own writes. */
let lastSelfWriteAt: string | null = null;
export function getLastSelfWriteAt(): string | null {
  return lastSelfWriteAt;
}

/** Patch AP: cloud-write gate. */
let cloudLoaded = false;
export function setCloudLoaded(v: boolean): void {
  cloudLoaded = v;
}
export function isCloudLoaded(): boolean {
  return cloudLoaded;
}

/** Patch AS: view-as-client gate. */
let viewingAs = false;
export function setSyncViewingAs(v: boolean): void {
  viewingAs = v;
}
export function isSyncViewingAs(): boolean {
  return viewingAs;
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
let saveError: Error | null = null;
let inFlight = false;

// Patch ET/EV: auto-retry timer and attempt counter.
// retryTimer: scheduled after each failed save; cancelled on user edit.
// retryAttempt: incremented on each failure, resets on success or user edit.
// Delay = min(retryAttempt * 20s, 120s): 20s -> 40s -> 60s -> ... -> 120s.
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryAttempt = 0;

// Patch DN: wall-clock timestamp of when the current in-flight save
// started. Used by the visibility-resume handler to decide whether the
// in-flight save has been stuck long enough that the PWA likely
// background-suspended in the middle of it. Set whenever inFlight
// flips true, never cleared (it is gated by the inFlight check before
// being read so its value when inFlight===false is irrelevant).
let inFlightStartedAt = 0;

// Patch DN: per-save generation token. Incremented on each new save
// AND on every resume-recovery so a zombied save's late-resolving
// continuation cannot clobber the state of a fresh save that started
// after the reset.
//
// Without this, the race is: (1) Save A's await hangs on a dead iOS
// socket. (2) visibilitychange fires, resume-handler resets inFlight
// to false. (3) User edits; Save B starts and runs to completion,
// notify('saved'), inFlight=false in finally. (4) Save A's await
// finally resolves (or its watchdog finally fires); its continuation
// has !watchdogFired so it calls notify('saved') again (status stays
// 'saved', fine) BUT its finally block also sets inFlight=false even
// though no save is actually in flight (also fine here, but if the
// timing were different and Save B's continuation were still
// pending, Save A's finally would have invalidated Save B's inFlight
// flag mid-save, allowing a third save to start concurrently).
//
// With the generation token, Save A's continuation checks
// `saveGeneration !== mygen` and exits before touching anything.
let saveGeneration = 0;

// Patch EA: track when the PWA was last hidden so we can detect a
// genuine wake-from-background (vs a quick tab toggle). After 60+s
// hidden, the underlying TCP/TLS connections to Supabase are likely
// dead (iOS aggressively reaps sockets during PWA suspension; OS-level
// keepalive timers are paused). The next request on a dead socket
// hangs until either browser-level detection (~30s) or our 15s
// Patch DF timeout fires, both of which manifest as the "stuck on
// saving then sync error" symptom that survives DU and DX.
//
// We use this flag to (a) trigger a prewarm query on visibilitychange
// after a long hidden period, and (b) allow flushCloudSave to retry
// once on timeout-style errors while still within the wake window.
let lastHiddenAt = 0;
let wakeRetryAllowedUntil = 0;

// Patch BQ: 1500 -> 500ms. See file-header comment for rationale.
const SAVE_DEBOUNCE_MS = 500;

// Patch BQ: latest scheduled (userId, state) pair stashed for the
// beforeunload flush. Without this, an edit made within the debounce
// window before tab close would not reach cloud since the timer hadn't
// fired. Local storage still has it, but next sign-in would pull the
// older cloud row and overwrite the local edit.
let pendingArgs: { userId: string; tenant: string; state: unknown } | null = null;

export type SyncStatus = 'idle' | 'saving' | 'saved' | 'error' | 'offline';

const listeners = new Set<(status: SyncStatus, error: Error | null) => void>();
let lastStatus: SyncStatus = 'idle';

function notify(status: SyncStatus, error: Error | null = null): void {
  lastStatus = status;
  saveError = error;
  for (const cb of listeners) cb(status, error);
}

export function onSyncStatus(cb: (status: SyncStatus, error: Error | null) => void): () => void {
  listeners.add(cb);
  cb(lastStatus, saveError);
  return () => { listeners.delete(cb); };
}

export function scheduleCloudSave(userId: string | null, tenant: string, state: unknown): void {
  if (!supabase || !userId) return;
  if (!cloudLoaded) return;
  if (viewingAs) return;

  // Patch BQ: stash latest args so beforeunload can flush a pending save.
  pendingArgs = { userId, tenant, state };

  // Patch ET/EV: cancel any pending auto-retry; the user's edit will be
  // saved by the debounced flush below. Reset retryAttempt so a fresh
  // failure sequence starts from the 20s base delay, not mid-progression.
  if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
  retryAttempt = 0;

  // Patch DP: self-heal on user interaction. DN already added a
  // visibilitychange + pageshow recovery path, but iOS PWA sometimes
  // drops BOTH events on wake (no fanfare, just "you're back, figure
  // it out"). When that happens the watchdog timer was also dropped
  // by the OS, so inFlight stays true forever and the user is
  // permanently stuck on 'saving'.
  //
  // The user typing another character into a field is the strongest
  // "I am back and I expect this to work" signal available to us -
  // it bypasses every iOS event-firing quirk because it routes
  // through the React event system, which runs synchronously inside
  // a user interaction. If we're in the stuck-save window when an
  // edit arrives, force-recover here so the new edit can proceed
  // instead of bouncing off the inFlight guard forever.
  //
  // The 10s threshold matches the DN visibilitychange recovery so
  // both paths use the same definition of "stuck". resumeRecover
  // already kicks a fresh flushCloudSave with the latest pendingArgs
  // (which we just set above to the current edit), so we return here
  // to avoid the debounced save scheduling a duplicate.
  if (inFlight && Date.now() - inFlightStartedAt > 10_000) {
    resumeRecover();
    return;
  }

  // Patch EZ-2: notify immediately so the sync indicator reflects a pending
  // save without waiting for the 500ms debounce to fire. The user has an
  // unsaved edit; 'saving' is the honest state. flushCloudSave also calls
  // notify('saving') when the network request actually starts -- idempotent.
  notify('saving');

  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => { void flushCloudSave(userId, tenant, state); }, SAVE_DEBOUNCE_MS);
}

async function flushCloudSave(userId: string, tenant: string, state: unknown): Promise<void> {
  if (inFlight) {
    saveTimer = setTimeout(() => { void flushCloudSave(userId, tenant, state); }, 200);
    return;
  }
  // Patch DN: mygen captures the generation at save start. All later
  // state mutations check `saveGeneration !== mygen` and exit silently
  // if this save has been invalidated by the resume-recovery handler.
  const mygen = ++saveGeneration;
  inFlight = true;
  inFlightStartedAt = Date.now();
  notify('saving');

  // Patch EL: outer 20s timeout via Promise.race. The previous approach
  // used a side-effecting setTimeout (watchdog) that set watchdogFired=true,
  // cleared inFlight, and notified error -- then the catch block hit
  // `if (watchdogFired) return` before the retry path. By replacing it
  // with Promise.race the timeout surfaces as a thrown error that catch
  // handles normally, so the retry fires on timeout just like any other
  // network failure.
  let outerTimeoutHandle: ReturnType<typeof setTimeout> | null = null;
  const outerTimeout = new Promise<never>((_, reject) => {
    outerTimeoutHandle = setTimeout(
      () => reject(new Error('save-timeout: 14s elapsed')),
      14_000,
    );
  });

  try {
    await Promise.race([saveStateToCloud(userId, tenant, state), outerTimeout]);
    if (saveGeneration !== mygen) return;
    retryAttempt = 0;
    notify('saved');
    // Successful write means pendingArgs no longer represents an
    // unsaved-to-cloud edit; clear so beforeunload doesn't double-send.
    pendingArgs = null;
  } catch (e) {
    if (saveGeneration !== mygen) return;
    // Patch ER: clear the outer timer immediately to prevent an unhandled
    // rejection from the still-pending outerTimeout promise.
    if (outerTimeoutHandle) { clearTimeout(outerTimeoutHandle); outerTimeoutHandle = null; }
    // Log for on-device diagnosis.
    if (typeof localStorage !== 'undefined') {
      try {
        const err = toError(e);
        localStorage.setItem('btc:sync-err', JSON.stringify({ t: new Date().toISOString(), msg: err.message }));
      } catch { /* ignore quota errors */ }
    }
    notify('error', toError(e));
    // Patch ET/EV: schedule retry outside the async call stack so catch
    // returns immediately and inFlight clears in the outer finally.
    // Progressive delay: 20s -> 40s -> 60s -> ... -> 120s (capped).
    // First retry fires at t=35s (15s timeout + 20s delay), which is 4s
    // after the standard 5-retransmit TCP eviction (t=31s). If that retry
    // also fails (6-retransmit device, eviction at t=63s), the second retry
    // fires at t=35+15+40=90s on a clean connection.
    ++retryAttempt;
    // Patch EX: after two consecutive timeouts, reload the page.
    //
    // Root cause: AbortController cancels the HTTP/2 stream but leaves the
    // dead TCP socket in the browser connection pool (keyed by origin). Every
    // subsequent request reuses that same dead socket and also hangs. There is
    // no JavaScript API to evict a TCP socket; the only reliable escape is a
    // page reload, which clears the pool entirely.
    //
    // Guards: only reload if (a) navigator.onLine is not false (not a genuine
    // offline scenario -- the pool is fine, just no network), and (b) the last
    // reload was >90s ago (prevents a reload loop if the network is broken
    // even after a fresh pool).
    //
    // AuthGate handles the reload by checking btc:reload-to-recover: it skips
    // resetToDefaults() and cloud-state overlay, uploading local state to cloud
    // on the fresh connection instead. User data (latest edit) is preserved in
    // localStorage by zustand persist and is therefore safe through the reload.
    if (
      retryAttempt >= 1 &&
      pendingArgs &&
      typeof window !== 'undefined' &&
      typeof localStorage !== 'undefined' &&
      navigator.onLine !== false
    ) {
      try {
        const lastReload = localStorage.getItem('btc:last-reload');
        const msSinceLast = lastReload ? Date.now() - parseInt(lastReload, 10) : Infinity;
        if (msSinceLast > 90_000) {
          localStorage.setItem('btc:reload-to-recover', '1');
          localStorage.setItem('btc:last-reload', String(Date.now()));
          window.location.reload();
          return; // outer finally still runs, clearing inFlight before reload
        }
      } catch { /* localStorage unavailable -- fall through to normal retry */ }
    }

    if (retryTimer) clearTimeout(retryTimer);
    if (pendingArgs) {
      const delay = Math.min(retryAttempt * 20_000, 120_000);
      const { userId: retryUserId, tenant: retryTenant, state: retryState } = pendingArgs;
      retryTimer = setTimeout(() => {
        retryTimer = null;
        void flushCloudSave(retryUserId, retryTenant, retryState);
      }, delay);
    }
  } finally {
    if (outerTimeoutHandle) clearTimeout(outerTimeoutHandle);
    // Patch DN/EL: only clear inFlight if THIS save is still the current
    // generation. Patch EL: no watchdogFired guard -- finally is now the
    // sole path that clears inFlight.
    if (saveGeneration === mygen) inFlight = false;
  }
}

/** Patch BQ: register a beforeunload handler that flushes a pending
 *  debounced save when the tab closes. Idempotent; safe to call multiple
 *  times. AuthGate calls this once after a successful sign-in.
 *
 *  beforeunload during page navigation is best-effort - the browser may
 *  or may not let the network call complete. Local-first persistence
 *  remains the source of truth; this just keeps cloud current in the
 *  edit-then-close edge case. */
let beforeUnloadInstalled = false;
export function installBeforeUnloadFlush(): void {
  if (beforeUnloadInstalled) return;
  if (typeof window === 'undefined') return; // SSR / test env
  beforeUnloadInstalled = true;
  window.addEventListener('beforeunload', () => {
    if (!pendingArgs) return;
    if (!cloudLoaded || viewingAs) return;
    if (saveTimer) clearTimeout(saveTimer);
    // Fire-and-forget; can't await in beforeunload.
    void saveStateToCloud(pendingArgs.userId, pendingArgs.tenant, pendingArgs.state).catch(() => {
      // Swallow: no UI to surface this and the user is leaving.
    });
  });
}

/** Patch AP: immediate cloud save for recovery paths (resetToSeed,
 *  importFromJson). Bypasses the debounce AND the cloudLoaded gate. */
export async function saveStateToCloudImmediate(userId: string, tenant: string, state: unknown): Promise<void> {
  if (!supabase) return;
  if (viewingAs) return;
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  pendingArgs = null;
  notify('saving');
  try {
    await saveStateToCloud(userId, tenant, state);
    notify('saved');
  } catch (e) {
    const err = toError(e);
    notify('error', err);
    throw err;
  }
}

// ----------------------------------------------------------------------
// Patch DN: PWA resume recovery for stuck saves.
//
// The DF watchdog (20s setTimeout inside flushCloudSave) is the existing
// guarantee against permanently-stuck 'saving' state - but it's a
// setTimeout, and iOS Safari PWAs sometimes drop scheduled timers
// entirely during long background-suspension instead of just pausing
// them. When that happens the watchdog never fires on wake, inFlight
// stays true forever, the 200ms-retry chain in flushCloudSave bounces
// off the inFlight guard forever, and the UI is permanently glued to
// 'saving' until the user kills the PWA process.
//
// The reliable wake signal is visibilitychange itself, not anything
// derived from a setTimeout. So: on every visibility-becomes-visible,
// if we still have a save marked in-flight AND wall-clock has elapsed
// past a threshold suggesting we crossed a background-suspend boundary,
// force-reset the stuck state and let the next edit re-trigger a save.
//
// The threshold needs to be longer than a healthy save's full lifecycle
// (which the DF inner 15s timeout caps anyway) but shorter than a user's
// patience. 10s is the right tradeoff: a save that legitimately took 10
// seconds AND happened to coincide with a tab switch will get a spurious
// recovery, but that's strictly safer than leaving the user permanently
// stuck. The cloud row is unchanged either way; the worst case is a
// status flap on the indicator before the next edit triggers a fresh
// save that succeeds.
function resumeRecover(): void {
  if (!inFlight) return;
  const elapsed = Date.now() - inFlightStartedAt;
  if (elapsed < 10_000) return;
  // Invalidate the zombied save's continuation so its eventual resolve
  // (if it ever resolves) cannot clobber inFlight or notify a stale
  // status. The fresh save started below gets a higher generation.
  saveGeneration++;
  inFlight = false;
  notify('error', new Error('Save interrupted by background suspend, retrying.'));
  // If we have unsaved state queued, kick a fresh save immediately
  // rather than waiting for the next edit. This is the typical case:
  // the user edited just before suspending the PWA, the save started,
  // the suspension killed the socket, the watchdog timer was dropped,
  // and we're now back on the foreground with stale pendingArgs.
  if (pendingArgs) {
    const { userId, tenant, state } = pendingArgs;
    void flushCloudSave(userId, tenant, state);
  }
}

// Patch EZ: proactive reload after long background to flush the stale TCP
// connection pool before the user makes an edit or signs out. The EX/EY
// reactive reload only fires when a save times out with a pending edit;
// read-only sessions and gaps between edits never trigger it, leaving a
// dead socket pool that hangs sign-out and the next save attempt.
//
// Thresholds  -  conservative enough to not fire on normal tab-switching:
//   Mobile (touch/coarse-pointer): 5 minutes (was 2; raised because brief
//     phone locks and quick task-switches hit the old 2min too easily)
//   Desktop/other:                10 minutes (was 4; normal work involves
//     switching browser tabs for several minutes at a time)
//
// Uses the same btc:reload-to-recover and btc:last-reload guards as EX
// so AuthGate preserves local state and the 90s cooldown prevents loops.
//
// Returns true if a reload was initiated (caller should return immediately).
function proactiveReloadIfStale(hiddenForMs: number): boolean {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return false;
  if (navigator.onLine === false) return false;
  const isMobile = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  const thresholdMs = isMobile ? 5 * 60_000 : 10 * 60_000;
  if (hiddenForMs < thresholdMs) return false;
  try {
    const lastReload = localStorage.getItem('btc:last-reload');
    const msSinceLast = lastReload ? Date.now() - parseInt(lastReload, 10) : Infinity;
    if (msSinceLast <= 90_000) return false;
    localStorage.setItem('btc:reload-to-recover', '1');
    localStorage.setItem('btc:last-reload', String(Date.now()));
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

// Install once at module load. typeof document check guards SSR / test
// environments where document is undefined. The listener is intentionally
// module-scoped (not React-effect-driven) because the stuck state we are
// recovering is itself module-scoped (inFlight, saveGeneration).
let resumeListenerInstalled = false;
function installResumeListener(): void {
  if (resumeListenerInstalled) return;
  if (typeof document === 'undefined') return;
  resumeListenerInstalled = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      // Patch EA: stamp hidden-at so the visible branch can decide
      // whether this is a real wake (long absence) vs a quick tab
      // toggle (no prewarm needed).
      lastHiddenAt = Date.now();
      return;
    }
    // visibilityState === 'visible'
    const hiddenFor = lastHiddenAt > 0 ? Date.now() - lastHiddenAt : 0;
    lastHiddenAt = 0;
    resumeRecover();
    // Patch EZ: reload if backgrounded long enough that the TCP pool is
    // likely stale. Returns true if reload was initiated; skip prewarm.
    if (proactiveReloadIfStale(hiddenFor)) return;
    // Patch EA: real wake -> prewarm the Supabase connection. The
    // first network request after a long PWA background often hangs
    // on a half-open TCP socket (iOS reaps sockets aggressively during
    // suspension but does not notify the JS layer). By firing a cheap
    // query here, one of two things happens:
    //   - It succeeds: connection is healthy; the user's actual save
    //     (if any) runs normally.
    //   - It fails fast: the browser detects the dead socket, drops it
    //     from the connection pool, and the next request gets a new
    //     fresh connection.
    // Either way, the user's actual save attempt no longer races a
    // stuck socket. We also stamp a wake-recency window. Patch EK
    // decoupled this from the retry path - retry now fires on any
    // timeout-style error regardless of window - but the window stays
    // useful as a hint for any future logic that wants to know
    // "did we recently wake from a long sleep". Five minutes covers
    // realistic user interaction patterns: wake PWA, scroll / read,
    // make first edit.
    // Patch EM: lowered threshold from 60_000 to 5_000. Semi-stale (30-50s
    // background, e.g. user locks phone briefly) never triggered the prewarm
    // at 60s, leaving a dead socket in the pool for the next save attempt.
    // Any background period longer than 5s is enough for iOS to reap TCP
    // sockets, so 5s is the right threshold.
    if (hiddenFor > 5_000 && supabase) {
      wakeRetryAllowedUntil = Date.now() + 5 * 60_000;
      // Patch EN: abort the prewarm after 8s. Without this, a hanging
      // prewarm stream keeps the HTTP/2 connection alive even after the
      // save's 15s AbortController fires. With both streams cancelled,
      // the browser tears down the dead connection and the retry gets a
      // fresh socket.
      const prewarmCtrl = new AbortController();
      setTimeout(() => prewarmCtrl.abort(), 8_000);
      void supabase
        .from('app_state')
        .select('user_id')
        .limit(1)
        .abortSignal(prewarmCtrl.signal)
        .then(
          () => {/* connection good */},
          () => {/* dead or aborted; browser has handled it */},
        );
    }
  });
  // pageshow with persisted=true catches iOS bfcache restorations that
  // visibilitychange sometimes misses (same WebKit quirk that
  // useAutoRefresh accounts for). The recovery is idempotent so getting
  // both events from a single wake is fine.
  window.addEventListener('pageshow', (event: PageTransitionEvent) => {
    if (event.persisted) {
      // Patch EZ: compute hiddenFor from lastHiddenAt before resetting it.
      // Reset here so if visibilitychange also fires for this same wake,
      // its hiddenFor = 0 and it won't double-reload.
      const hiddenFor = lastHiddenAt > 0 ? Date.now() - lastHiddenAt : 0;
      lastHiddenAt = 0;
      if (proactiveReloadIfStale(hiddenFor)) return;
      // Patch EA: bfcache restoration is also a wake. Stamp the
      // wake-recency window (Patch EK extended from 30s to 5min; see
      // visibilitychange branch for rationale). Also fire the same
      // prewarm since the socket pool is just as stale after bfcache
      // restore.
      wakeRetryAllowedUntil = Date.now() + 5 * 60_000;
      if (supabase) {
        // Patch EN: same 8s abort as the visibilitychange prewarm.
        const prewarmCtrl = new AbortController();
        setTimeout(() => prewarmCtrl.abort(), 8_000);
        void supabase
          .from('app_state')
          .select('user_id')
          .limit(1)
          .abortSignal(prewarmCtrl.signal)
          .then(() => {/* good */}, () => {/* dead or aborted */});
      }
      resumeRecover();
    }
  });
}
installResumeListener();

// Patch EU: heartbeat that catches stuck saves missed by all other recovery
// paths. setInterval callbacks are deferred during iOS suspension but NOT
// dropped -- they fire on resume. So this catches cases where both the 15s/20s
// timers were dropped AND visibilitychange/pageshow did not fire.
if (typeof setInterval !== 'undefined') {
  setInterval(() => {
    if (inFlight && Date.now() - inFlightStartedAt > 20_000) {
      resumeRecover();
    }
  }, 5_000);
}

// Patch EZ-2: connection keepalive. The Supabase proxy (PostgREST + nginx)
// closes idle HTTP/2 connections after ~60-90s of inactivity. The next save
// after a reading/viewing pause hits the dead socket and hangs for the full
// abort timeout (12s) before a reload recovers it. A lightweight SELECT every
// 60s while visible keeps the connection warm so saves that follow a reading
// pause are near-instant rather than triggering the timeout+reload path.
// Only runs while visible and not already in-flight to avoid competing with
// an active save. Dead-socket detection still handled by AbortController; this
// is purely proactive.
if (typeof setInterval !== 'undefined') {
  setInterval(() => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    if (inFlight) return;
    if (!supabase) return;
    const ctrl = new AbortController();
    setTimeout(() => ctrl.abort(), 5_000);
    void supabase
      .from('app_state')
      .select('user_id')
      .limit(1)
      .abortSignal(ctrl.signal)
      .then(() => {}, () => {});
  }, 60_000);
}
